import React from 'react';
import { Chart } from '../engine/chart';
import { signOf } from '../engine/aspects';
import { PLANETS } from '../data/constants';

interface Props {
  chart: Chart;
  className?: string;
}

/** Points whose position is approximate or algorithm-dependent. */
const PRECISION_NOTES: Record<string, string> = {
  Chiron: 'Chiron is computed analytically and can differ by up to ~0.4° from ephemeris tables.',
  Lilith: 'Mean Black Moon Lilith — the mean lunar apogee. Some tools show the osculating ("true") apogee instead.',
  Node: 'Lunar node. Switch between True and Mean Node above.',
  SouthNode: 'Always exactly opposite the North Node.',
  Fortune: 'Lot of Fortune — a derived point, not a body.',
};

/**
 * Compact legend listing every point on the wheel with glyph, name, sign, and degree.
 * Designed to sit beside the ChartWheel so users can map glyphs to placements.
 */
export const PlanetLegend: React.FC<Props> = ({ chart, className }) => {
  const entries = Object.entries(chart.placements).sort((a, b) => a[1].longitude - b[1].longitude);

  return (
    <div className={`rounded-lg border border-border/60 bg-card/40 p-3 text-sm ${className ?? ''}`}>
      <ul className="space-y-1.5">
        {entries.map(([name, p]) => {
          const def = PLANETS.find((pl) => pl.name === name);
          if (!def) return null;
          const s = signOf(p.longitude);
          const deg = Math.floor(s.degree);
          const min = Math.floor((s.degree - deg) * 60);
          const note = PRECISION_NOTES[name];
          return (
            <li key={name} className="flex items-center gap-2 leading-tight" title={note}>
              <span
                className="font-serif text-base w-5 text-center shrink-0"
                style={{ color: def.color }}
              >
                {def.glyph}
              </span>
              <span className="w-16 shrink-0 text-foreground/90">
                {name === 'SouthNode' ? 'S. Node' : name}
              </span>
              <span className="font-serif text-foreground">{s.glyph}</span>
              <span className="text-muted-foreground tabular-nums">
                {deg}°{String(min).padStart(2, '0')}′
              </span>
              {p.retrogradeUnknown
                ? <span className="text-xs text-muted-foreground" title="Direct or retrograde motion was not supplied with this imported chart">℞?</span>
                : p.retrograde && <span className="text-xs text-muted-foreground">℞</span>}
              {note && <span className="sr-only">{note}</span>}
            </li>
          );
        })}
      </ul>
      {chart.unknownTime && (
        <p className="mt-2 text-xs text-muted-foreground">
          Birth time unknown — positions are cast for local noon. The Moon can be up to 6° away from its
          true position, so its sign is not guaranteed.
        </p>
      )}
      {chart.mcDerived && (
        <p className="mt-2 text-xs text-muted-foreground">
          No Midheaven was supplied with this chart, so it is derived from the Ascendant.
        </p>
      )}
    </div>
  );
};